import { useState, useEffect } from 'react';
import moment from 'moment';
import { PercentIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import TableHeader from '../../components/generic/TableHeader';
import SubjectTabs from '../generic/SubjectTabs';
import StatCell from '../generic/StatCell';
import TableLoader from '../generic/TableLoader';
import NoData from '../generic/NoData';
import { QSTATUS } from '../../../../../apps/frontend/src/utils/strings';
import { findPercentile } from '../../../../../apps/frontend/src/utils/percentile';

const PerformanceTestBreakdown = ({ pack, data, loading, showPercentage, onTogglePercentage, onResetFilters }) => {
  const [activeSubject, setActiveSubject] = useState('overall');
  const [showPercentile, setShowPercentile] = useState(false);
  const [headers, setHeaders] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (data?.progress?.length > 0) {
      const allSubjects = new Map();
      data.progress.forEach(({ detailedAnalysis }) => {
        if (detailedAnalysis?.subjectWise) {
          detailedAnalysis.subjectWise.forEach((subject) => {
            if (!allSubjects.has(subject.subjectMeta._id)) {
              allSubjects.set(subject.subjectMeta._id, {
                _id: subject.subjectMeta._id,
                title: subject.subjectMeta.title,
                icon: subject.subjectMeta.icon,
                darkIcon: subject.subjectMeta.darkIcon,
                color: subject.subjectMeta.color,
              });
            }
          });
        }
      });
      setHeaders(Array.from(allSubjects.values()));
    }
  }, [data]);

  useEffect(() => {
    if (
      activeSubject === 'overall' &&
      (pack?.title?.includes('JEE Main 2025 Full Test Series') ||
        pack?.title?.includes('JEE Main 2025 Mini Test Series'))
    ) {
      setShowPercentile(true);
    } else {
      setShowPercentile(false);
    }
  }, [activeSubject, pack?.title]);

  const getPercentileText = (percentileData, score) => {
    const percentile = findPercentile({
      data: percentileData?.data,
      score: Number(score),
    });
    return percentile?.title ? `${percentile.title} ${percentile?.data}` : percentile?.data ? percentile?.data : '-';
  };

  return (
    <div>
      <TableHeader
        headerTitle="Test-wise Breakdown"
        headerSwitch={
          <span className="flex items-center gap-1">
            <PercentIcon size={16} weight="bold" />
            Show Percentage
          </span>
        }
        headerSwitchCase={showPercentage}
        headerSwitchHandler={onTogglePercentage}
        showBottomBorder={false}
      />

      {/* SubjectTabs */}
      <div className="overflow-x-auto border-b border-r border-l border-[var(--q3-stroke-light)] bg-white mb-[-1px]">
        <div className="min-w-0 md:min-w-0">
          <SubjectTabs subjects={headers} activeSubject={activeSubject} onSubjectChange={setActiveSubject} />
        </div>
      </div>

      {/* Main content */}
      <div className="overflow-x-auto">
        <div className="min-w-[800px] md:min-w-0 border-l border-r border-b border-[var(--q3-stroke-light)] rounded-b-lg overflow-hidden">
          {/* Table Header */}
          <div className="bg-[var(--q3-surface-dim)] border-b border-[var(--q3-stroke-light)]">
            <div
              className={`grid ${
                showPercentile ? 'grid-cols-[220px_1fr_1fr_1fr_1fr_1fr_1fr]' : 'grid-cols-[220px_1fr_1fr_1fr_1fr_1fr]'
              } divide-x divide-[var(--q3-stroke-light)] text-sm font-medium text-[var(--q3-neutral-default)]`}
            >
              <div className="p-4 flex items-center justify-start">Test Title</div>

              {showPercentile && (
                <div className="p-4 text-center text-nowrap text-purple-600">
                  <div>Percentile</div>
                </div>
              )}

              <div className="p-4 text-center text-[var(--q3-orange-normal)]">
                <div>Total</div>
                <div>Score</div>
              </div>
              <div className="p-4 text-center text-[var(--q3-green-normal)]">
                <div>Attempted</div>
                <div>Correct</div>
              </div>
              <div className="p-4 text-center text-[var(--q3-danger-normal)]">
                <div>Attempted</div>
                <div>Wrong</div>
              </div>
              <div className="p-4 text-center text-[var(--q3-indigo-normal)]">
                <div>Not</div>
                <div>Attempted</div>
              </div>
              <div className="p-4 text-center text-[var(--q3-neutral-light)]">
                <div>Not</div>
                <div>Visited Qs</div>
              </div>
            </div>
          </div>

          {/* Table Body */}
          <TableLoader loading={loading}>
            {data?.progress?.length === 0 && <NoData onResetAll={onResetFilters} />}
            {data?.progress?.map(({ testId, detailedAnalysis }, index) => {
              const subjectData =
                activeSubject === 'overall'
                  ? detailedAnalysis.subjectWise.find((subject) => subject.subjectMeta._id === 'overall')
                  : detailedAnalysis.subjectWise.find((subject) => subject.subjectMeta._id === activeSubject);

              if (!subjectData) return null;

              return (
                <div
                  key={index}
                  onClick={
                    () => navigate(`/${pack?._id}/${testId}/analysis`) // ✅ fixed
                  }
                  className={`grid ${
                    showPercentile
                      ? 'grid-cols-[220px_1fr_1fr_1fr_1fr_1fr_1fr]'
                      : 'grid-cols-[220px_1fr_1fr_1fr_1fr_1fr]'
                  } divide-x divide-[var(--q3-stroke-light)] text-sm text-[var(--q3-neutral-default)] border-b border-[var(--q3-stroke-light)] last:border-b-0 cursor-pointer hover:bg-[var(--q3-fill-light-hover)] transition`}
                >
                  <div className="p-4">
                    <div className="font-medium truncate max-w-full">{detailedAnalysis?.testTitle}</div>
                    <div className="text-xs text-[var(--q3-neutral-light)]">
                      {moment(detailedAnalysis?.submittedAt).format('D MMM YYYY') +
                        (detailedAnalysis.isBonusAvailable && detailedAnalysis.isBonusAttempted
                          ? ' | Bonus Qs Attempted'
                          : '')}
                    </div>
                  </div>

                  {showPercentile && (
                    <div className="p-4 text-center font-medium text-purple-600">
                      {detailedAnalysis?.percentileData?.isAvailable
                        ? getPercentileText(
                            detailedAnalysis?.percentileData?.percentileData,
                            subjectData?.totalMarkScored,
                          )
                        : '-'}
                    </div>
                  )}

                  <div className="text-center text-orange-600">
                    <StatCell
                      color="orange"
                      top={subjectData?.totalMarkScored}
                      bottom={subjectData?.totalMarks}
                      showPercentage={showPercentage}
                      {...QSTATUS.AVERAGE}
                    />
                  </div>

                  <div className="text-center text-green-600">
                    <StatCell
                      color="green"
                      top={subjectData?.correct}
                      bottom={subjectData?.totalQuestions}
                      showPercentage={showPercentage}
                      {...QSTATUS.CORRECT}
                    />
                  </div>

                  <div className="text-center text-red-600">
                    <StatCell
                      color="red"
                      top={subjectData?.incorrect}
                      bottom={subjectData?.totalQuestions}
                      showPercentage={showPercentage}
                      {...QSTATUS.INCORRECT}
                    />
                  </div>

                  <div className="text-center text-blue-600">
                    <StatCell
                      color="blue"
                      top={subjectData?.unanswered}
                      bottom={subjectData?.totalQuestions}
                      showPercentage={showPercentage}
                      {...QSTATUS.NOT_ATTEMPTED}
                    />
                  </div>

                  <div className="text-center text-gray-600">
                    <StatCell
                      color="gray"
                      top={subjectData?.notVisited}
                      bottom={subjectData?.totalQuestions}
                      showPercentage={showPercentage}
                      {...QSTATUS.NOT_VISITED}
                      themedText={true}
                    />
                  </div>
                </div>
              );
            })}
          </TableLoader>
        </div>
      </div>
    </div>
  );
};

export default PerformanceTestBreakdown;
